import React from 'react';

import {
  Card,
  CardContent,
  CardHeader,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import {useIntl, defineMessages} from 'react-intl';
import {useQuery} from 'react-query';

import API, {Types, GroupVehiclePaginationProps} from '@wheeebo/polaris-api';
import {CircleLoader, LoadError, SquareChip, VehiclePanel} from '@wheeebo/polaris-ui';

const INITIAL_ITEM_COUNT = 10;

const msgs = defineMessages({
  cellName: {
    id: 'pages.member.member.GroupListCard.cell.name',
    defaultMessage: 'Name',
  },
  cellRole: {
    id: 'pages.member.member.GroupListCard.cell.role',
    defaultMessage: 'Role',
  },
});

interface RowProps {
  groupVehicle: Types.GroupVehicle;
  onSelect: () => void;
}

function Row({groupVehicle, onSelect}: RowProps) {
  return (
    <TableRow hover sx={{cursor: 'pointer'}} onClick={onSelect}>
      <TableCell>
        <VehiclePanel vehicle={groupVehicle.vehicle} showActive />
      </TableCell>
      <TableCell>
        <SquareChip
          label={groupVehicle.vehicle?.activationConfirmed ? 'Activated' : 'Not-activated'}
          color={groupVehicle.vehicle?.activationConfirmed ? 'primary' : 'error'}
        />
      </TableCell>
    </TableRow>
  );
}

interface Props {
  groupId: Types.UUID;
  onVehicleSelected: (vehicle: Types.UUID) => void;
}

const GroupVehicleListCard = ({groupId, onVehicleSelected}: Props): JSX.Element => {
  const intl = useIntl();
  const [page, setPage] = React.useState(0);
  const options: GroupVehiclePaginationProps = {
    limit: INITIAL_ITEM_COUNT,
    offset: page * INITIAL_ITEM_COUNT,
    preload: ['vehicle'],
  };
  const {data, isError, isLoading} = useQuery(API.GroupVehicles.listKey(groupId, options), () =>
    API.GroupVehicles.list(groupId, options),
  );
  return (
    <Card variant="outlined">
      <CardHeader title="Vehicles" subheader="The vehicles this group owns." />
      <CardContent>
        {isLoading ? <CircleLoader /> : null}
        {isError ? <LoadError /> : null}
        {data && data.total > 0 ? (
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{intl.formatMessage(msgs.cellName)}</TableCell>
                  <TableCell>{intl.formatMessage(msgs.cellRole)}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.items?.map((e: Types.GroupVehicle) => (
                  <Row key={e.id} groupVehicle={e} onSelect={() => onVehicleSelected(e.vehicle.id)} />
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[INITIAL_ITEM_COUNT]}
                    count={data?.total || 0}
                    rowsPerPage={INITIAL_ITEM_COUNT}
                    page={page}
                    SelectProps={{
                      inputProps: {'aria-label': 'rows per page'},
                    }}
                    onPageChange={(_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => setPage(newPage)}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2">No record found.</Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default React.memo(GroupVehicleListCard);
